import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api';
import ProductCard from '../components/ProductCard';
import { useAuth } from '../context/AuthContext';

const LIMIT = 20;

export default function Shop() {
  const { user } = useAuth();
  const [isVIP, setIsVIP] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const sentinelRef = useRef(null);

  const category = searchParams.get('category') || '';
  const search = searchParams.get('search') || '';
  const sort = searchParams.get('sort') || 'newest';
  const minPrice = searchParams.get('min_price') || '';
  const maxPrice = searchParams.get('max_price') || '';
  const vipOnly = searchParams.get('vip_only') === '1';
  const [priceRange, setPriceRange] = useState([minPrice || '', maxPrice || '']);
  const [showPriceFilter, setShowPriceFilter] = useState(false);

  useEffect(() => {
    if (user) api.get('/membership').then(r => { if (r.data?.status === 'active') setIsVIP(true); }).catch(() => {});
  }, [user]);

  const PRICE_PRESETS = [
    { label: 'Under $5', min: '', max: '5' },
    { label: '$5–$15', min: '5', max: '15' },
    { label: '$15–$30', min: '15', max: '30' },
    { label: '$30–$50', min: '30', max: '50' },
    { label: 'Over $50', min: '50', max: '' },
  ];

  const applyPrice = (min, max) => {
    const params = new URLSearchParams(searchParams);
    if (min) params.set('min_price', min); else params.delete('min_price');
    if (max) params.set('max_price', max); else params.delete('max_price');
    setSearchParams(params);
    setShowPriceFilter(false);
  };

  useEffect(() => {
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  // Dynamic SEO title + meta per category/search
  useEffect(() => {
    let title, desc;
    if (search) {
      title = `Search: "${search}" – StyleMe`;
      desc = `Shop StyleMe for "${search}" — discover fashion, jewelry and accessories at unbeatable prices.`;
    } else if (category && categories.length > 0) {
      const cat = categories.find(c => c.slug === category);
      title = cat ? `${cat.name} – StyleMe` : 'Shop All Products – StyleMe';
      desc = cat ? `Shop ${cat.name} at StyleMe — huge selection, low prices, free shipping over $50.` : 'Shop all women\'s fashion, jewelry and accessories at StyleMe.';
    } else {
      title = 'Shop All Products – StyleMe';
      desc = 'Discover 10,000+ women\'s fashion, jewelry and accessories at StyleMe. New arrivals daily, free shipping over $50, easy returns.';
    }
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
    meta.content = desc;
    return () => { document.title = 'StyleMe - Shop | Style | Shine'; meta.content = ''; };
  }, [category, search, categories]);

  // Reset when filters change
  useEffect(() => {
    setProducts([]);
    setPage(1);
    setHasMore(true);
  }, [category, search, sort, minPrice, maxPrice, vipOnly]);

  // Fetch a page
  const fetchPage = useCallback((pageNum) => {
    if (loading) return;
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set('category', category);
    if (search) params.set('search', search);
    if (minPrice) params.set('min_price', minPrice);
    if (maxPrice) params.set('max_price', maxPrice);
    if (vipOnly) params.set('vip_only', '1');
    params.set('sort', sort);
    params.set('page', pageNum);
    params.set('limit', LIMIT);

    api.get(`/products?${params}`).then(r => {
      const newProducts = r.data.products;
      setProducts(prev => pageNum === 1 ? newProducts : [...prev, ...newProducts]);
      setTotal(r.data.pagination.total);
      setHasMore(pageNum < r.data.pagination.pages);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [category, search, sort, minPrice, maxPrice, vipOnly]);

  useEffect(() => { fetchPage(page); }, [page, fetchPage]);

  // Infinite scroll sentinel
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        setPage(p => p + 1);
      }
    }, { threshold: 0.1 });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, loading]);

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    setSearchParams(params);
  };

  const hasActiveFilters = category || search || minPrice || maxPrice;

  return (
    <div>
      {/* ── Shop Hero ── */}
      <div style={{ background:'linear-gradient(135deg,#1e1b4b,#4c1d95)', padding:'36px 24px 32px', color:'white' }}>
        <div className="container" style={{ maxWidth:1100 }}>
          <h1 style={{ fontSize:'clamp(24px,4vw,38px)', fontWeight:900, margin:'0 0 8px', letterSpacing:-0.5 }}>
            {search ? `Search: "${search}"` : category ? (categories.find(c => c.slug === category)?.name || 'All Products') : '✨ Shop All Styles'}
          </h1>
          <p style={{ opacity:0.8, fontSize:15, margin:0 }}>
            {search ? `Showing results for "${search}"` : category ? 'Browse our curated collection' : 'Discover your next favourite piece'}
            {' · New arrivals added daily'}
          </p>
        </div>
      </div>

      {/* ── Category pills ── */}
      <div style={{ background:'white', borderBottom:'1px solid #f3f4f6', overflowX:'auto', whiteSpace:'nowrap' }}>
        <div style={{ display:'inline-flex', gap:8, padding:'12px 24px', maxWidth:1100, margin:'0 auto' }}>
          <button onClick={() => updateFilter('category', '')}
            style={{ padding:'7px 18px', borderRadius:100, border:'1.5px solid', fontWeight:600, fontSize:13, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0,
              borderColor: !category ? '#4f46e5' : '#e5e7eb', background: !category ? '#4f46e5' : 'white', color: !category ? 'white' : '#374151' }}>
            All Products
          </button>
          {categories.filter(c => c.slug !== 'uncategorized' && c.product_count > 0).map(c => (
            <button key={c.id} onClick={() => updateFilter('category', c.slug)}
              style={{ padding:'7px 18px', borderRadius:100, border:'1.5px solid', fontWeight:600, fontSize:13, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0,
                borderColor: category === c.slug ? '#4f46e5' : '#e5e7eb', background: category === c.slug ? '#4f46e5' : 'white', color: category === c.slug ? 'white' : '#374151' }}>
              {c.name}
            </button>
          ))}
          {isVIP && (
            <button onClick={() => { const p = new URLSearchParams(searchParams); vipOnly ? p.delete('vip_only') : p.set('vip_only','1'); setSearchParams(p); }}
              style={{ padding:'7px 18px', borderRadius:100, border:'1.5px solid', fontWeight:700, fontSize:13, cursor:'pointer', whiteSpace:'nowrap', flexShrink:0,
                borderColor: vipOnly ? '#4f46e5' : '#a78bfa', background: vipOnly ? '#4f46e5' : '#ede9fe', color: vipOnly ? 'white' : '#4f46e5' }}>
              💎 VIP Only
            </button>
          )}
        </div>
      </div>

      <div className="container" style={{ maxWidth:1100, paddingTop:24, paddingBottom:40 }}>
        {/* ── Sort + price filter bar ── */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:12 }}>
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            {/* Price filter button */}
            <div style={{ position:'relative' }}>
              <button onClick={() => setShowPriceFilter(v => !v)}
                style={{ padding:'8px 14px', border:`1.5px solid ${(minPrice||maxPrice) ? '#4f46e5' : '#e5e7eb'}`, borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer',
                  background:(minPrice||maxPrice) ? '#fef9e7' : 'white', color:(minPrice||maxPrice) ? '#4f46e5' : '#374151' }}>
                💰 Price {minPrice || maxPrice ? `($${minPrice||'0'}–${maxPrice ? '$'+maxPrice : '∞'})` : '▾'}
              </button>
              {showPriceFilter && (
                <div style={{ position:'absolute', top:'calc(100% + 6px)', left:0, background:'white', border:'1.5px solid #e5e7eb', borderRadius:12, padding:16, zIndex:100, minWidth:220, boxShadow:'0 8px 24px rgba(0,0,0,0.12)' }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#6b7280', marginBottom:10, textTransform:'uppercase', letterSpacing:1 }}>Quick Select</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:14 }}>
                    {PRICE_PRESETS.map(p => (
                      <button key={p.label} onClick={() => applyPrice(p.min, p.max)}
                        style={{ padding:'5px 12px', borderRadius:100, border:'1.5px solid #e5e7eb', fontSize:12, fontWeight:600, cursor:'pointer', background:'white', color:'#374151' }}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize:12, fontWeight:700, color:'#6b7280', marginBottom:8, textTransform:'uppercase', letterSpacing:1 }}>Custom Range</div>
                  <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10 }}>
                    <input type="number" placeholder="Min $" value={priceRange[0]}
                      onChange={e => setPriceRange([e.target.value, priceRange[1]])}
                      style={{ width:'80px', padding:'6px 8px', border:'1.5px solid #e5e7eb', borderRadius:6, fontSize:13 }} />
                    <span style={{ color:'#9ca3af' }}>–</span>
                    <input type="number" placeholder="Max $" value={priceRange[1]}
                      onChange={e => setPriceRange([priceRange[0], e.target.value])}
                      style={{ width:'80px', padding:'6px 8px', border:'1.5px solid #e5e7eb', borderRadius:6, fontSize:13 }} />
                    <button onClick={() => applyPrice(priceRange[0], priceRange[1])}
                      style={{ padding:'6px 14px', background:'#4f46e5', color:'white', border:'none', borderRadius:6, fontWeight:700, fontSize:12, cursor:'pointer' }}>Go</button>
                  </div>
                  {(minPrice||maxPrice) && (
                    <button onClick={() => applyPrice('','')} style={{ fontSize:12, color:'#ef4444', background:'none', border:'none', cursor:'pointer', padding:0, fontWeight:600 }}>✕ Clear price filter</button>
                  )}
                </div>
              )}
            </div>
            {hasActiveFilters && (
              <button onClick={() => { setSearchParams({}); setPriceRange(['','']); }}
                style={{ fontSize:12, color:'#ef4444', background:'none', border:'1px solid #fecaca', borderRadius:100, padding:'3px 10px', cursor:'pointer', fontWeight:600 }}>
                ✕ Clear all
              </button>
            )}
          </div>
          <select value={sort} onChange={e => updateFilter('sort', e.target.value)}
            style={{ padding:'8px 14px', border:'1.5px solid #e5e7eb', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', background:'white' }}>
            <option value="newest">Newest First</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="name">Name A-Z</option>
          </select>
        </div>

        {products.length === 0 && loading ? (
          // Skeleton grid
          <div className="products-grid">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} style={{ borderRadius: 12, overflow: 'hidden', background: 'white', border: '1px solid #f3f4f6' }}>
                <div className="skeleton" style={{ width: '100%', aspectRatio: '3/4' }} />
                <div style={{ padding: '12px 14px 16px' }}>
                  <div className="skeleton" style={{ height: 14, width: '80%', marginBottom: 8 }} />
                  <div className="skeleton" style={{ height: 14, width: '50%', marginBottom: 12 }} />
                  <div className="skeleton" style={{ height: 20, width: '35%' }} />
                </div>
              </div>
            ))}
          </div>
        ) : products.length === 0 && !loading ? (
          <div className="empty-state">
            <div className="icon">🔍</div>
            <h3>No products found</h3>
            <p>Try adjusting your filters or search terms.</p>
          </div>
        ) : (
          <>
            <div className="products-grid">
              {products.map(p => <ProductCard key={p.id} product={p} />)}
            </div>
            <div ref={sentinelRef} style={{ height:40, marginTop:20 }} />
            {loading && (
              <div style={{ display:'flex', justifyContent:'center', padding:'20px 0' }}>
                <div className="spinner" />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
